import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import type { CallState, IncidentStatus, UnitStatus } from '../lib/types';
import { titleCase } from '../lib/format';

/** Shared presentational primitives. No data fetching, no business rules. */

export function Card({
  title,
  actions,
  children,
  flush,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions}
        </div>
      )}
      <div className={`card-body${flush ? ' flush' : ''}`}>{children}</div>
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

export function Banner({ kind, children }: { kind: 'ok' | 'error' | 'warn'; children: ReactNode }) {
  return (
    <div className={`banner ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Modal({
  title,
  onClose,
  footer,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  // Escape closes: an operator interrupted by a call needs the overlay gone
  // without hunting for a button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={wide ? { maxWidth: 860 } : undefined} role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <div className="spacer" />
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; badge?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={tab.id === active ? 'active' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.badge ? ` (${tab.badge})` : ''}
        </button>
      ))}
    </div>
  );
}

/** Two-step destructive action — no modal, but not a single stray click either. */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  className = 'btn danger sm',
  disabled,
}: {
  label: string;
  confirmLabel?: string;
  onConfirm: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={() => {
        if (armed) {
          onConfirm();
          setArmed(false);
        } else {
          setArmed(true);
        }
      }}
    >
      {armed ? (confirmLabel ?? 'Confirm?') : label}
    </button>
  );
}

// ---------------------------------------------------------------- status badges

const INCIDENT_TONE: Record<IncidentStatus, string> = {
  draft: 'neutral',
  pending: 'warn',
  dispatched: 'accent',
  en_route: 'accent',
  on_scene: 'accent',
  resolved: 'ok',
  closed: 'neutral',
  cancelled: 'neutral',
};

export function IncidentBadge({ status }: { status: IncidentStatus }) {
  return <span className={`badge ${INCIDENT_TONE[status]}`}>{titleCase(status)}</span>;
}

const UNIT_TONE: Record<UnitStatus, string> = {
  available: 'ok',
  assigned: 'accent',
  en_route: 'accent',
  on_scene: 'accent',
  busy: 'warn',
  out_of_service: 'danger',
  off_duty: 'neutral',
};

export function UnitBadge({ status }: { status: UnitStatus }) {
  return (
    <span className={`badge ${UNIT_TONE[status]}`}>
      <i className="dot" />
      {titleCase(status)}
    </span>
  );
}

const CALL_TONE: Record<CallState, string> = {
  ringing: 'danger',
  queued: 'warn',
  connected: 'ok',
  on_hold: 'warn',
  parked: 'warn',
  transferring: 'accent',
  ended: 'neutral',
  abandoned: 'danger',
  voicemail: 'neutral',
};

export function CallBadge({ state }: { state: CallState }) {
  return <span className={`badge ${CALL_TONE[state]}`}>{titleCase(state)}</span>;
}

export function PriorityPill({
  code,
  color,
  level,
}: {
  code: string | null;
  color: string | null;
  level?: number | null;
}) {
  if (!code) return <span className="badge neutral">Unclassified</span>;
  return (
    <span className="pill-priority" style={{ background: color ?? '#6b7280' }} title={`Priority level ${level ?? '?'}`}>
      {code}
    </span>
  );
}

/** Red when the dispatch clock has run out, amber when it is close. */
export function SlaBadge({
  dueAt,
  breachedAt,
  dispatchedAt,
  now,
}: {
  dueAt: string | null;
  breachedAt: string | null;
  dispatchedAt: string | null;
  now: number;
}) {
  if (dispatchedAt) return null;
  if (breachedAt) return <span className="badge danger">SLA breached</span>;
  if (!dueAt) return null;
  const secondsLeft = Math.round((new Date(dueAt).getTime() - now) / 1000);
  if (secondsLeft <= 0) return <span className="badge danger">SLA due</span>;
  const label = secondsLeft >= 60 ? `${Math.floor(secondsLeft / 60)}m` : `${secondsLeft}s`;
  return <span className={`badge ${secondsLeft < 120 ? 'warn' : 'neutral'}`}>SLA {label}</span>;
}
